import { useEffect, useState, useMemo } from "react";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { PageHeader } from "../components/ui/page-header";
import { EmptyState } from "../components/ui/empty-state";
import {
    ArchiveRestore, Trash2, Loader2, Search, History, RefreshCw, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { formatRupiah, formatTanggal } from "../lib/format";

const JENIS_LABEL = {
    bon_kain: "Bon Kain",
    bon_peralatan: "Bon Peralatan",
    setor_barang: "Setor Barang",
    kasbon: "Kasbon",
    setor_perbaikan: "Setor Perbaikan",
    totalan: "Totalan",
    reject: "Rijek (+ Bon RJB)",
};

const JENIS_COLOR = {
    totalan: "#0071E3",
    reject: "#ec4899",
    bon_kain: "var(--apple-blue)",
    bon_peralatan: "#8b5cf6",
    setor_barang: "#10b981",
    kasbon: "#f59e0b",
    setor_perbaikan: "#14b8a6",
};

const PAGE_SIZE = 20;

const fmtDateTime = (iso) => {
    if (!iso) return "-";
    try {
        const d = new Date(iso);
        return d.toLocaleString("id-ID", {
            day: "2-digit", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
};

export default function RiwayatTerhapus() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [jenisFilter, setJenisFilter] = useState("all");
    const [vendors, setVendors] = useState([]);
    const [vendorFilter, setVendorFilter] = useState("all");
    const [page, setPage] = useState(1);

    const [restoring, setRestoring] = useState(null); // row being restored
    const [confirmPerm, setConfirmPerm] = useState(null); // row for permanent delete

    // v1.8.12 — guard dihapus; semua authenticated user boleh akses Riwayat Terhapus
    // (restore + permanent delete). Account Center tetap owner-only.

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (jenisFilter !== "all") params.jenis = jenisFilter;
            if (vendorFilter !== "all") params.vendor_id = vendorFilter;
            const { data } = await api.get("/trash", { params });
            setItems(data || []);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        api.get("/vendors").then((r) => setVendors(r.data || [])).catch(() => {});
    }, []);
    useEffect(() => { load(); setPage(1); /* eslint-disable-next-line */ }, [jenisFilter, vendorFilter]);

    const vendorMap = useMemo(() => Object.fromEntries(vendors.map((v) => [v.id, v.nama])), [vendors]);

    const filtered = useMemo(() => {
        if (!q.trim()) return items;
        const needle = q.toLowerCase();
        return items.filter((r) =>
            (r._nomor || "").toLowerCase().includes(needle) ||
            (vendorMap[r.vendor_id] || "").toLowerCase().includes(needle) ||
            (r.deleted_by || "").toLowerCase().includes(needle),
        );
    }, [items, q, vendorMap]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageItems = useMemo(() => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [filtered, page]);

    const doRestore = async (row) => {
        setRestoring(row.id);
        try {
            await api.post(`/trash/${row._jenis}/${row.id}/restore`);
            toast.success(`${JENIS_LABEL[row._jenis]} ${row._nomor} dipulihkan`);
            load();
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setRestoring(null); }
    };

    const doPermanent = async () => {
        if (!confirmPerm) return;
        setRestoring(confirmPerm.id);
        try {
            await api.delete(`/trash/${confirmPerm._jenis}/${confirmPerm.id}/permanent`);
            toast.success("Transaksi dihapus permanen");
            setConfirmPerm(null);
            load();
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setRestoring(null); }
    };

    if (user === null) {
        return <div className="p-8 text-sm" style={{ color: "var(--text-muted)" }}>Memuat…</div>;
    }

    return (
        <div data-testid="riwayat-terhapus-page">
            <PageHeader
                title="Riwayat Terhapus"
                description="Semua transaksi yang dihapus 30 hari terakhir — pulihkan atau hapus permanen."
                actions={
                    <Button
                        onClick={load}
                        variant="outline"
                        data-testid="riwayat-refresh"
                        className="rounded-lg gap-2"
                    >
                        <RefreshCw className="w-4 h-4" /> Muat Ulang
                    </Button>
                }
            />

            {/* Owner banner */}
            <div
                className="rounded-xl p-3 mb-4 flex items-start gap-3"
                style={{ background: "rgba(245, 158, 11, 0.08)", border: "1px solid rgba(245, 158, 11, 0.25)" }}
            >
                <ShieldAlert className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: "#f59e0b" }} />
                <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
                    Area <b style={{ color: "var(--text-primary)" }}>Owner Only</b>. Hapus permanen tidak bisa di-undo.
                    Transaksi yang dipulihkan akan muncul kembali di buku vendor + perhitungan plafon seperti sedia kala.
                </div>
            </div>

            {/* Filters */}
            <div className="rounded-xl bg-white p-4 mb-4" style={{ border: "1px solid var(--border)" }}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
                        <Input
                            placeholder="Cari nomor / vendor / dihapus-oleh…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            data-testid="riwayat-search"
                            className="pl-9 rounded-lg h-10"
                        />
                    </div>
                    <Select value={jenisFilter} onValueChange={setJenisFilter}>
                        <SelectTrigger className="rounded-lg h-10" data-testid="riwayat-jenis-filter">
                            <SelectValue placeholder="Semua jenis" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Semua jenis</SelectItem>
                            {Object.entries(JENIS_LABEL).map(([k, v]) => (
                                <SelectItem key={k} value={k}>{v}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={vendorFilter} onValueChange={setVendorFilter}>
                        <SelectTrigger className="rounded-lg h-10" data-testid="riwayat-vendor-filter">
                            <SelectValue placeholder="Semua vendor" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Semua vendor</SelectItem>
                            {vendors.map((v) => (
                                <SelectItem key={v.id} value={v.id}>{v.nama}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Desktop table */}
            <div className="hidden md:block rounded-xl bg-white overflow-hidden" style={{ border: "1px solid var(--border)" }}>
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wider" style={{ color: "var(--text-muted)", background: "var(--bg-subtle)" }}>
                            <th className="px-4 py-3 font-medium">Jenis</th>
                            <th className="px-4 py-3 font-medium">Nomor</th>
                            <th className="px-4 py-3 font-medium">Vendor</th>
                            <th className="px-4 py-3 font-medium">Tanggal Transaksi</th>
                            <th className="px-4 py-3 font-medium text-right">Nilai</th>
                            <th className="px-4 py-3 font-medium">Dihapus Oleh</th>
                            <th className="px-4 py-3 font-medium">Dihapus Kapan</th>
                            <th className="px-4 py-3 font-medium text-right">Aksi</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={8} className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline" style={{ color: "var(--apple-blue)" }} /></td></tr>
                        ) : pageItems.length === 0 ? (
                            <tr><td colSpan={8}><EmptyState icon={History} title="Riwayat Terhapus kosong" description={q || jenisFilter !== "all" || vendorFilter !== "all" ? "Tidak ada hasil untuk filter ini." : "Transaksi yang dihapus akan muncul di sini selama 30 hari."} testid="riwayat-empty" /></td></tr>
                        ) : pageItems.map((row) => (
                            <tr key={`${row._jenis}-${row.id}`} className="border-t" style={{ borderColor: "var(--border-soft)" }} data-testid={`riwayat-row-${row.id}`}>
                                <td className="px-4 py-3">
                                    <span
                                        className="inline-block px-2 py-0.5 rounded-md text-[11px] font-medium"
                                        style={{ background: `${JENIS_COLOR[row._jenis]}18`, color: JENIS_COLOR[row._jenis] }}
                                    >
                                        {JENIS_LABEL[row._jenis]}
                                    </span>
                                </td>
                                <td className="px-4 py-3 font-mono text-[12px]" style={{ color: "var(--text-primary)" }}>{row._nomor}</td>
                                <td className="px-4 py-3 truncate max-w-[180px]" style={{ color: "var(--text-primary)" }}>{vendorMap[row.vendor_id] || "-"}</td>
                                <td className="px-4 py-3" style={{ color: "var(--text-secondary)" }}>{formatTanggal(row._tanggal)}</td>
                                <td className="px-4 py-3 text-right num-tabular font-medium" style={{ color: "var(--text-primary)" }}>{formatRupiah(row._nilai)}</td>
                                <td className="px-4 py-3 text-xs" style={{ color: "var(--text-secondary)" }}>{row.deleted_by || "-"}</td>
                                <td className="px-4 py-3 text-xs" style={{ color: "var(--text-muted)" }}>{fmtDateTime(row.deleted_at)}</td>
                                <td className="px-4 py-3 text-right">
                                    <div className="flex items-center justify-end gap-1">
                                        <Button
                                            size="sm"
                                            onClick={() => doRestore(row)}
                                            disabled={restoring === row.id}
                                            data-testid={`restore-btn-${row.id}`}
                                            className="rounded-lg h-8 gap-1 text-white"
                                            style={{ background: "#059669" }}
                                        >
                                            {restoring === row.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArchiveRestore className="w-3.5 h-3.5" />}
                                            Pulihkan
                                        </Button>
                                        <button
                                            onClick={() => setConfirmPerm(row)}
                                            data-testid={`perm-btn-${row.id}`}
                                            className="p-2 rounded-lg hover:bg-[var(--apple-red-soft)]"
                                            style={{ color: "var(--apple-red)" }}
                                            title="Hapus Permanen"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden space-y-2">
                {loading ? (
                    <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--apple-blue)" }} /></div>
                ) : pageItems.length === 0 ? (
                    <div className="rounded-xl bg-white" style={{ border: "1px solid var(--border)" }}>
                        <EmptyState icon={History} title="Riwayat Terhapus kosong" description="Transaksi yang dihapus akan muncul di sini selama 30 hari." testid="riwayat-empty-mobile" />
                    </div>
                ) : pageItems.map((row) => (
                    <div
                        key={`${row._jenis}-${row.id}`}
                        className="rounded-xl bg-white p-4"
                        style={{ border: "1px solid var(--border)" }}
                        data-testid={`riwayat-card-${row.id}`}
                    >
                        <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="min-w-0 flex-1">
                                <span
                                    className="inline-block px-2 py-0.5 rounded-md text-[10px] font-medium mb-1"
                                    style={{ background: `${JENIS_COLOR[row._jenis]}18`, color: JENIS_COLOR[row._jenis] }}
                                >
                                    {JENIS_LABEL[row._jenis]}
                                </span>
                                <div className="font-mono text-xs" style={{ color: "var(--apple-blue)" }}>{row._nomor}</div>
                                <div className="text-xs truncate" style={{ color: "var(--text-primary)" }}>{vendorMap[row.vendor_id] || "-"}</div>
                            </div>
                            <div className="text-right">
                                <div className="num-tabular font-semibold text-sm" style={{ color: "var(--text-primary)" }}>{formatRupiah(row._nilai)}</div>
                                <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{formatTanggal(row._tanggal)}</div>
                            </div>
                        </div>
                        <div className="text-[11px] mb-3" style={{ color: "var(--text-muted)" }}>
                            Dihapus oleh <b style={{ color: "var(--text-secondary)" }}>{row.deleted_by || "-"}</b> · {fmtDateTime(row.deleted_at)}
                        </div>
                        <div className="flex items-center justify-end gap-2">
                            <button
                                onClick={() => setConfirmPerm(row)}
                                className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1"
                                style={{ color: "var(--apple-red)", background: "var(--apple-red-soft)" }}
                                data-testid={`perm-btn-mobile-${row.id}`}
                            >
                                <Trash2 className="w-3.5 h-3.5" /> Permanen
                            </button>
                            <Button
                                size="sm"
                                onClick={() => doRestore(row)}
                                disabled={restoring === row.id}
                                data-testid={`restore-btn-mobile-${row.id}`}
                                className="rounded-lg h-8 gap-1 text-white"
                                style={{ background: "#059669" }}
                            >
                                {restoring === row.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArchiveRestore className="w-3.5 h-3.5" />}
                                Pulihkan
                            </Button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-end gap-2 text-sm" style={{ color: "var(--text-secondary)" }}>
                    <span>Halaman {page} / {totalPages}</span>
                    <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg">Sebelumnya</Button>
                    <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-lg">Berikutnya</Button>
                </div>
            )}

            {/* Confirm permanent delete */}
            <AlertDialog open={!!confirmPerm} onOpenChange={(o) => !o && setConfirmPerm(null)}>
                <AlertDialogContent className="bg-white rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <ShieldAlert className="w-5 h-5" style={{ color: "var(--apple-red)" }} />
                            Hapus Permanen?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            <span className="font-semibold" style={{ color: "var(--text-primary)" }}>{confirmPerm?._nomor}</span> ({JENIS_LABEL[confirmPerm?._jenis] || "-"}) akan dihapus <b>selamanya</b> dari database. Aksi ini <b>tidak bisa dibatalkan</b>.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl" data-testid="confirm-perm-cancel">Batal</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={doPermanent}
                            disabled={restoring === confirmPerm?.id}
                            data-testid="confirm-perm-action"
                            className="rounded-xl text-white"
                            style={{ background: "var(--apple-red)" }}
                        >
                            {restoring === confirmPerm?.id ? "Menghapus…" : "Hapus Permanen"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
